import React, { memo, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import {
  BASE,
  BORDER,
  BRAND,
  GREY,
  PRIMARY,
  SUCCESS,
} from "../../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../../utils/constants/UI";
import IconComponent from "../../../../../components/Icon/IconComponent";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import { Button } from "../../../../../components/Buttons";
import CustomModal from "../../../../../components/Modals/CustomModal";
import { formatCredits } from "./billingUtils";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatPrice = (plan) => {
  const sym = plan?.currency === "USD" || !plan?.currency ? "$" : plan.currency;
  return `${sym}${plan?.price ?? 0}`;
};

const intervalSuffix = (interval) => (interval === "year" ? "/yr" : "/mo");

const BillingInviteMemberModal = memo(
  ({
    open,
    onClose,
    packages = [],
    currentSeats = 0,
    onInvite,
  }) => {
    const [email, setEmail] = useState("");
    const [touched, setTouched] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [success, setSuccess] = useState(false);
    const [seatPlanId, setSeatPlanId] = useState(null);
    const [billingCycle, setBillingCycle] = useState("month");

    // Non-SDR, non-free packages - split by billing interval
    const allSeatPlans = useMemo(
      () => (packages || []).filter((p) => !p.isSdr && !p.isFree),
      [packages],
    );

    const hasYearly = useMemo(
      () => allSeatPlans.some((p) => p.interval === "year"),
      [allSeatPlans],
    );
    const hasMonthly = useMemo(
      () => allSeatPlans.some((p) => p.interval === "month"),
      [allSeatPlans],
    );

    const seatPlans = useMemo(() => {
      const filtered = allSeatPlans.filter((p) => p.interval === billingCycle);
      if (filtered.length) return filtered;
      // Fallback: if the chosen cycle has nothing, show whatever is available
      return allSeatPlans.length ? allSeatPlans : packages || [];
    }, [allSeatPlans, billingCycle, packages]);

    const validEmail = EMAIL_RE.test(email.trim());
    const selectedSeatPlan =
      seatPlans.find((p) => p.id === seatPlanId) || seatPlans[0] || null;

    useEffect(() => {
      if (open) {
        setEmail("");
        setTouched(false);
        setIsProcessing(false);
        setSuccess(false);
        // Default to monthly when both exist; otherwise pick the one available
        if (hasMonthly) setBillingCycle("month");
        else if (hasYearly) setBillingCycle("year");
      }
    }, [open, hasMonthly, hasYearly]);

    useEffect(() => {
      if (!open) return;
      const featured = seatPlans.find((p) => p.featured);
      setSeatPlanId((featured || seatPlans[0])?.id || null);
    }, [open, seatPlans]);

    const handleClose = () => {
      if (!isProcessing) onClose?.();
    };

    const buyAndInvite = () => {
      setTouched(true);
      if (!validEmail || isProcessing || !selectedSeatPlan) return;
      setIsProcessing(true);
      setTimeout(() => {
        setIsProcessing(false);
        setSuccess(true);
        onInvite?.({
          email: email.trim(),
          seatPlan: selectedSeatPlan,
        });
      }, 1000);
    };

    const renderFooter = () => {
      if (success) {
        return {
          right: (
            <Box style={{ width: "fit-content" }}>
              <Button
                label="Done"
                variant="primary"
                size="sm36"
                onClick={handleClose}
              />
            </Box>
          ),
        };
      }
      return {
        left: (
          <Box style={{ width: "fit-content" }}>
            <Button
              label="Cancel"
              variant="ghost"
              size="sm36"
              onClick={handleClose}
              disabled={isProcessing}
            />
          </Box>
        ),
        right: (
          <Box style={{ width: "fit-content" }}>
            <Button
              label={
                selectedSeatPlan
                  ? `Buy ${selectedSeatPlan.name} & Invite · ${formatPrice(selectedSeatPlan)}${intervalSuffix(selectedSeatPlan.interval)}`
                  : "Buy Seat & Invite"
              }
              variant="primary"
              size="sm36"
              isLoading={isProcessing}
              disabled={!validEmail || isProcessing || !selectedSeatPlan}
              onClick={buyAndInvite}
            />
          </Box>
        ),
      };
    };

    return (
      <CustomModal
        open={open}
        close={handleClose}
        onClose={handleClose}
        header={{
          heading: {
            id: "invite_member",
            label: success ? "Invite sent" : "Invite a team member",
          },
          description: !success && {
            id: "invite_desc",
            label: "They'll get an email to join your workspace.",
          },
        }}
        width="540px"
        footer={renderFooter()}
      >
        {success ? (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
              padding: "20px 8px 8px",
            }}
          >
            <Box
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                background: SUCCESS.W_50,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconComponent
                iconLabel={ICON_LABELS.CHECK_CIRCLE}
                style={{ color: SUCCESS.W_500, fontSize: "32px" }}
              />
            </Box>
            <TypographyInline
              size="md"
              fontWeight={fontWeight.SEMIBOLD}
              color={GREY.PRIMARY}
              label={`Invite sent to ${email.trim()}`}
            />
            <TypographyInline
              size="sm"
              color={GREY.SECONDARY}
              label={
                selectedSeatPlan
                  ? `Added 1 ${selectedSeatPlan.name} seat (${formatPrice(selectedSeatPlan)}${intervalSuffix(selectedSeatPlan.interval)}, prorated). They'll appear as Pending until they accept.`
                  : "They'll appear as Pending in your team list until they accept."
              }
            />
          </Box>
        ) : (
          <Box style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
            {/* Plan picker - every invite buys a plan */}
            <Box style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 14px",
                  background: BASE.BACKGROUND_LIGHT,
                  border: `1px solid ${BORDER.LIGHT}`,
                  borderRadius: "10px",
                }}
              >
                <IconComponent
                  iconLabel={ICON_LABELS.GROUPS}
                  style={{
                    color: GREY.TERTIARY,
                    fontSize: "18px",
                  }}
                />
                <TypographyInline
                  size="sm"
                  color={GREY.PRIMARY}
                  label={`You have ${currentSeats} ${currentSeats === 1 ? "seat" : "seats"}.`}
                />
              </Box>

              {seatPlans.length > 0 ? (
                  <Box>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        marginBottom: "10px",
                        flexWrap: "wrap",
                      }}
                    >
                      <TypographyInline
                        size="xs"
                        fontWeight={fontWeight.BOLD}
                        color={GREY.TERTIARY}
                        label="CHOOSE A PLAN"
                      />
                      {(hasMonthly && hasYearly) && (
                        <Box
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            background: BASE.BACKGROUND_LIGHT,
                            border: `1px solid ${BORDER.DEFAULT}`,
                            borderRadius: "999px",
                            padding: "3px",
                          }}
                        >
                          {[
                            { key: "month", label: "Monthly" },
                            { key: "year", label: "Yearly" },
                          ].map((opt) => {
                            const active = billingCycle === opt.key;
                            return (
                              <Box
                                key={opt.key}
                                role="button"
                                tabIndex={0}
                                aria-pressed={active}
                                onClick={() => setBillingCycle(opt.key)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setBillingCycle(opt.key);
                                  }
                                }}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  padding: "5px 12px",
                                  borderRadius: "999px",
                                  cursor: "pointer",
                                  background: active
                                    ? BASE.WHITE
                                    : BASE.TRANSPARENT,
                                  border: active
                                    ? `1px solid ${BRAND.PRIMARY}`
                                    : `1px solid ${BASE.TRANSPARENT}`,
                                  boxShadow: active
                                    ? `0 8px 18px -18px ${GREY.SECONDARY}`
                                    : "none",
                                  transition:
                                    "background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
                                }}
                                sx={{
                                  "&:focus-visible": {
                                    outline: `2px solid ${BRAND.PRIMARY}`,
                                    outlineOffset: "-1px",
                                  },
                                }}
                              >
                                <TypographyInline
                                  size="xs"
                                  fontWeight={fontWeight.SEMIBOLD}
                                  color={active ? BRAND.PRIMARY : GREY.TERTIARY}
                                  label={opt.label}
                                />
                                {opt.key === "year" && (
                                  <span
                                    style={{
                                      fontSize: "9px",
                                      fontWeight: 700,
                                      background: active
                                        ? PRIMARY.W_100
                                        : SUCCESS.W_100,
                                      color: active
                                        ? BRAND.PRIMARY
                                        : SUCCESS.W_600,
                                      border: active
                                        ? `1px solid ${PRIMARY.W_200}`
                                        : `1px solid ${SUCCESS.W_200}`,
                                      padding: "1px 6px",
                                      borderRadius: "999px",
                                      letterSpacing: "0.2px",
                                    }}
                                  >
                                    SAVE 20%
                                  </span>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                    <Box
                      style={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${Math.min(seatPlans.length, 3)}, minmax(0, 1fr))`,
                        gap: "8px",
                      }}
                    >
                      {seatPlans.map((p) => {
                        const active = (selectedSeatPlan?.id) === p.id;
                        return (
                          <Box
                            key={p.id}
                            onClick={() => setSeatPlanId(p.id)}
                            role="radio"
                            aria-checked={active}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSeatPlanId(p.id);
                              }
                            }}
                            style={{
                              position: "relative",
                              padding: "12px 10px",
                              border: `1px solid ${active ? BRAND.PRIMARY : BORDER.DEFAULT}`,
                              borderRadius: "10px",
                              background: active ? PRIMARY.W_50 : BASE.WHITE,
                              cursor: "pointer",
                              transition:
                                "border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease",
                              boxShadow: active
                                ? `0 0 0 3px ${PRIMARY.W_50}`
                                : `0 6px 14px -16px ${GREY.SECONDARY}`,
                              textAlign: "center",
                              minWidth: 0,
                            }}
                            sx={{
                              "&:hover": {
                                borderColor: active
                                  ? BRAND.PRIMARY
                                  : BORDER.HOVER,
                                background: active
                                  ? PRIMARY.W_50
                                  : BASE.BACKGROUND_LIGHT,
                              },
                              "&:focus-visible": {
                                outline: `2px solid ${BRAND.PRIMARY}`,
                                outlineOffset: "2px",
                              },
                            }}
                          >
                            {p.featured && (
                              <Box
                                style={{
                                  position: "absolute",
                                  top: "-9px",
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  background: BRAND.PRIMARY,
                                  color: BASE.WHITE,
                                  padding: "2px 8px",
                                  borderRadius: "999px",
                                  fontSize: "9px",
                                  fontWeight: 700,
                                  letterSpacing: "0.3px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                POPULAR
                              </Box>
                            )}
                            <TypographyInline
                              size="sm"
                              fontWeight={fontWeight.SEMIBOLD}
                              color={active ? BRAND.PRIMARY : GREY.PRIMARY}
                              label={p.name}
                            />
                            <Box
                              style={{
                                marginTop: "4px",
                                display: "flex",
                                alignItems: "baseline",
                                justifyContent: "center",
                                gap: "2px",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              <TypographyInline
                                size="md"
                                fontWeight={fontWeight.BOLD}
                                color={GREY.PRIMARY}
                                label={formatPrice(p)}
                              />
                              <TypographyInline
                                size="xs"
                                color={GREY.TERTIARY}
                                label={intervalSuffix(p.interval)}
                              />
                            </Box>
                            {p.credits != null && (
                              <Box style={{ marginTop: "4px" }}>
                                <TypographyInline
                                  size="xs"
                                  color={GREY.SECONDARY}
                                  label={`${formatCredits(p.credits)} credits${intervalSuffix(p.interval)}`}
                                />
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Box
                    style={{
                      padding: "12px 14px",
                      border: `1px dashed ${BORDER.DEFAULT}`,
                      borderRadius: "10px",
                      background: BASE.BACKGROUND_LIGHT,
                    }}
                  >
                    <TypographyInline
                      size="sm"
                      color={GREY.SECONDARY}
                      label="No plans available right now. Please try again later."
                    />
                  </Box>
                )}
            </Box>

            {/* Email input */}
            <Box>
              <Box style={{ marginBottom: "6px" }}>
                <TypographyInline
                  size="xs"
                  fontWeight={fontWeight.BOLD}
                  color={GREY.TERTIARY}
                  label="EMAIL ADDRESS"
                />
              </Box>
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  border: `1px solid ${touched && !validEmail ? BRAND.PRIMARY : BORDER.DEFAULT}`,
                  borderRadius: "8px",
                  padding: "0 12px",
                  background: BASE.WHITE,
                  transition: "border-color 0.18s ease, box-shadow 0.18s ease",
                }}
                sx={{
                  "&:focus-within": {
                    borderColor: BRAND.PRIMARY,
                    boxShadow: `0 0 0 3px ${PRIMARY.W_50}`,
                  },
                }}
              >
                <IconComponent
                  iconLabel={ICON_LABELS.EMAIL}
                  style={{ color: GREY.TERTIARY, fontSize: "20px" }}
                />
                <input
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      buyAndInvite();
                    }
                  }}
                  placeholder="teammate@company.com"
                  disabled={isProcessing}
                  style={{
                    flex: 1,
                    border: "none",
                    outline: "none",
                    padding: "10px 0",
                    fontSize: "14px",
                    color: GREY.PRIMARY,
                    background: "transparent",
                    fontWeight: 500,
                  }}
                />
              </Box>
              {touched && !validEmail && (
                <Box style={{ marginTop: "6px" }}>
                  <TypographyInline
                    size="xs"
                    color={BRAND.PRIMARY}
                    label="Please enter a valid email address."
                  />
                </Box>
              )}
              <Box style={{ marginTop: "8px" }}>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  label="They'll get an email to join your workspace."
                />
              </Box>
            </Box>
          </Box>
        )}
      </CustomModal>
    );
  },
);

BillingInviteMemberModal.displayName = "BillingInviteMemberModal";

export default BillingInviteMemberModal;
